import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { sidebarQueryOptions } from '../queryOptions';
import { useCanVisit } from '../routing/canVisit';

import { visibleItems } from './sidebarItems';
import type { SidebarItem } from './sidebarItems';

export type SidebarState =
  | { readonly status: 'ready'; readonly items: readonly SidebarItem[] }
  | { readonly status: 'loading' }
  | { readonly status: 'failed'; readonly retry: () => void }
  | { readonly status: 'absent' };

const NO_ITEMS: readonly SidebarItem[] = [];

const LOADING: SidebarState = { status: 'loading' };

const ABSENT: SidebarState = { status: 'absent' };

export const useSidebarState = (): SidebarState => {
  const { data, status, fetchStatus, refetch } = useQuery(sidebarQueryOptions());
  const canVisit = useCanVisit();
  const items = useMemo(() => (data === undefined ? undefined : visibleItems(data, canVisit)), [data, canVisit]);
  if (items !== undefined) return { status: 'ready', items };
  if (status === 'error') return { status: 'failed', retry: () => void refetch() };
  return fetchStatus === 'idle' ? ABSENT : LOADING;
};

export const useSidebarItems = (): readonly SidebarItem[] => {
  const sidebar = useSidebarState();
  return sidebar.status === 'ready' ? sidebar.items : NO_ITEMS;
};
